import { useEffect, useState } from 'react';

import {
  IconCircle,
  IconCircleCheck,
  IconLink,
  IconNote,
} from '@tabler/icons-react';
import { IconCircleCheckDisabled, IconCircleDisabled } from 'icons';
import { useTranslation } from 'react-i18next';

import { Typography } from '@cfa/react-components';

import { cn } from '@/utils/tailwind';

import { itemByLanguage } from 'utils/language';

import PlanItemStepLink from './PlanItemStepLink';

import { PlanAssignment, PlanStep } from '@/_generated/api-clients/xp';

interface PlanItemTaskProps {
  disabled?: boolean;
  isActive: boolean;
  isComplete: boolean;
  language?: string;
  onClick: (step: PlanStep) => void;
  onCompleteToggle: () => void;
  planId: string;
  statuses: PlanAssignment[];
  step: PlanStep;
  userId?: string;
}

const PlanItemTask: React.FC<PlanItemTaskProps> = ({
  disabled,
  isActive,
  isComplete,
  language,
  onClick,
  onCompleteToggle,
  planId,
  statuses,
  step,
  userId,
}) => {
  const [isCompleted, setIsCompleted] = useState(isComplete);
  const { t } = useTranslation();
  const noteName = itemByLanguage(step.note, undefined, language);
  const firstUrlName = itemByLanguage(
    step.urls?.[0]?.urlSet,
    undefined,
    language,
  );
  const hasNotes = !!noteName;
  const hasUrls = !!firstUrlName;
  const canExpand = hasNotes || hasUrls;
  const userStatus = statuses
    .find(status => status.userId === userId)
    ?.steps?.find(statusStep => statusStep.stepId === step.id);
  // Use `duration` (API field) not `stepDuration` (legacy normalized field).
  // See PATHWAY-2935 for context.
  const stepDuration = userStatus?.duration
    ? Math.round(userStatus.duration / 60)
    : 0;

  useEffect(() => {
    setIsCompleted(isComplete);
  }, [isComplete]);

  const expandTask = isActive && (hasNotes || hasUrls);

  return (
    <>
      <div
        className={cn('flex w-full items-center gap-2', {
          'pb-2.5': expandTask,
        })}
      >
        {isCompleted ? (
          <button
            type="button"
            className={cn('mr-2!', { 'cursor-pointer': !disabled })}
            data-testid="StepStatusIcon"
            disabled={disabled}
            onClick={() => {
              setIsCompleted(prevState => !prevState);
              onCompleteToggle();
            }}
          >
            {!disabled ? (
              <IconCircleCheck className="text-feedback-success-base" />
            ) : (
              <IconCircleCheckDisabled className="text-grayscale-gray2" />
            )}
          </button>
        ) : (
          <button
            type="button"
            className={cn('mr-2!', { 'cursor-pointer': !disabled })}
            data-testid="StepStatusIcon"
            disabled={disabled}
            onClick={() => {
              setIsCompleted(prevState => !prevState);
              onCompleteToggle();
            }}
          >
            {!disabled ? (
              <IconCircle className="text-grayscale-gray2" />
            ) : (
              <IconCircleDisabled className="text-grayscale-gray2" />
            )}
          </button>
        )}

        <button
          type="button"
          className={cn('flex w-full flex-col text-left', {
            'cursor-pointer': canExpand && !disabled,
          })}
          data-testid={`PlanStep${step.id}`}
          disabled={(disabled ?? false) || !canExpand}
          onClick={() => onClick(step)}
        >
          <Typography data-testid="TaskTitle" variant="overline3">
            {t('Generic.task')}
            {isCompleted &&
              userId &&
              ` - ${t('Generic.completedIn')} ${stepDuration} ${t(
                'Generic.mins',
              )}`}
          </Typography>
          <Typography data-testid="TaskOrProcedureName" variant="body1">
            {itemByLanguage(step.name, undefined, language)}
          </Typography>
        </button>
        <button
          type="button"
          className={cn('text-grayscale-gray2 flex', {
            'cursor-pointer': canExpand && !disabled,
          })}
          disabled={(disabled ?? false) || !canExpand}
          onClick={() => onClick(step)}
        >
          {itemByLanguage(step.note, undefined, language) && <IconNote />}
          {hasUrls && <IconLink />}
        </button>
      </div>
      {expandTask && (
        <>
          <div className="flex w-full flex-col pl-10">
            {hasNotes && (
              <div className="pb-2.5">
                <Typography variant="overline3">
                  {t('Generic.notes')}
                </Typography>
                <Typography variant="body1">{noteName}</Typography>
              </div>
            )}
            {hasUrls &&
              step.urls?.map((link, index) => (
                <PlanItemStepLink
                  link={link}
                  language={language}
                  key={link.urlSet?.en ?? index}
                  planId={planId}
                  stepId={step.id}
                  stepName={itemByLanguage(step.name, undefined, language)}
                />
              ))}
          </div>
        </>
      )}
    </>
  );
};

export default PlanItemTask;
